import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';
import RoadmapPath from '../../assets/images/RoadmapPath.png';
import RoadmapPathComplete from '../../assets/images/RoadmapPathComplete.png';
import { useDispatch, useSelector } from 'react-redux';
import * as Immutable from 'immutable';
import * as appActions from '../../Actions/app';
import { LevelData, LevelStatus, UserLevelData } from '../../Types';
import { find } from 'lodash';
import '../../index.css';
import './Path.css';
import { useNavigate } from 'react-router-dom';
import { isCapacitorNative } from 'Utils/Constants';
import TouchBox from 'Components/Capacitor/TouchBox';
import { RootState } from 'Reducers';
import { CompletedChapters, resolvePlayRoute } from 'Utils/tutorialCompletion';
import LevelPreviewSheet, { LevelPreviewSheetState } from './LevelPreviewSheet';
import {
  RoadmapPathScale,
  clampMapTranslateY,
  roadmapPathImageHeight,
  roadmapPathScale,
} from './roadmapLayout';
import { LevelNameFit, fitLevelName, usableNameSizeSteps } from './levelNameFit';
import { isArrowForThePage } from './roadmapKeys';
import { useHardwareBackClosesLayer } from 'Hooks/useHardwareBack';

interface LevelDataProps {
  left: string;
  top: string;
  levelName: string;
  levelNumber: number;
  levelIndex: number;
  levelStatus: string;
  labelSide: string;
  isAccessible: boolean;
  highestTierComplete: number;
}

interface LevelBlobProps {
  levelSelect: number;
  data: LevelDataProps;
  levelCompletedAt: string | undefined;
  onSingleSelect: (levelIndex: number) => void;
  forceSingleTap: boolean;
  // At the small path scale the nodes sit close together: a name wider than
  // this wraps onto a second line (or, with no room for one, gets smaller)
  // instead of running into the next name on the same side of the path. Null
  // keeps the name on one line.
  nameWrapWidth: number | null;
  // The path area's top and bottom edges, in the same coordinates as the
  // node's top: names are kept inside them.
  pathAreaTop: number;
  pathAreaBottom: number;
}

const sheetPeekHeight = 136;
// The most of a portrait window the sheet covers at peek.
const sheetPeekPortraitShare = 0.5;

// The level name box (.blob-text-styles) and the least space kept between two names.
const nameLabelWidth = 120;
const nameGutter = 8;
// Padding between a name and its circle, the circle's size, and the path
// scroller's top and bottom padding (the edges that clip the names).
const labelPadding = 10;
const circleSize = 50;
const pathScrollerPadding = 50;
// Names are 14px or 16px and may be drawn down to 12px: the name is measured at its
// own size and at each smaller size it can be drawn at, one hidden line each.

// Past the last level of the unit the path keeps going for this many pixels and
// fades out, while a dark layer that starts at the same point covers the whole
// height of the window (header included) and darkens over it.
const endOfUnitFadeWidth = 400;
// Under the header (z-index 1 in Roadmap.css), the Play bar (fixed, later in
// the page), the sheet (1500) and the pull-out menu (1700).
const endOfUnitLayerZIndex = 0;
const endOfUnitShade = (fadeStart: number) =>
  `linear-gradient(90deg, rgba(0, 0, 0, 0) ${fadeStart}px, rgba(0, 0, 0, 0.68) ${
    fadeStart + 0.6521 * endOfUnitFadeWidth
  }px)`;

const endOffsetValues = {
  small: 20,
  'medium-small': 30,
  medium: 60,
  large: 60,
};

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

const readSafeInset = (cssVarName: string) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  const raw = window.getComputedStyle(document.documentElement).getPropertyValue(cssVarName).trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

// How far (px) a mouse press may move and still be a click rather than a drag of the map.
const mapDragClickSlop = 5;

// A node's centre is kept at least this far inside the visible part of the path area:
// the selected node's halo (31px from its centre) and a little room stay in view.
const mapEdgePadding = 40;

// The map's vertical offset as drawn (it eases between offsets), or the given fallback
// where the browser does not report it.
const drawnTranslateY = (mapLayer: Element | null, fallback: number): number => {
  if (!mapLayer) return fallback;
  const transform = window.getComputedStyle(mapLayer).transform ?? '';
  const matrix = /^matrix\(([^)]+)\)$/.exec(transform);
  if (matrix) return Number(matrix[1].split(',')[5]);
  const matrix3d = /^matrix3d\(([^)]+)\)$/.exec(transform);
  if (matrix3d) return Number(matrix3d[1].split(',')[13]);
  const translateY = /^translateY\((-?[\d.]+)px\)$/.exec(transform);
  if (translateY) return Number(translateY[1]);
  return fallback;
};

const LevelBlob = (props: LevelBlobProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Completed tutorial chapters for this blob's level (hydrated at startup), so a
  // double-click routes like every other Play surface - see resolvePlayRoute / MUS-393.
  const completedChapters = useSelector(
    (state: RootState) =>
      Immutable.get(state.tutorialReducer?.completed, props.data.levelNumber) as CompletedChapters,
  );

  // LevelStatus should have one of 3 values: "not-started", "in-progress", "complete"
  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);
  // A mouse or pen click selects a level without giving it focus, so Space then starts
  // the selected level's lesson as before. Focus moves instead to a plain spot just
  // after the level (not a control, not a Tab stop), so Tab goes on from the level that
  // was clicked, as it would from a click on the page there. Keyboard focus (Tab) and
  // touch taps are unchanged.
  const lastPointerType = useRef('');
  const focusRestAfterLevel = useRef<HTMLSpanElement | null>(null);
  const notePointerType = (event: React.PointerEvent<HTMLElement>) => {
    lastPointerType.current = event.pointerType;
  };
  const keepMousePressFromFocusing = (event: React.MouseEvent<HTMLElement>) => {
    if (lastPointerType.current !== 'mouse' && lastPointerType.current !== 'pen') return;
    event.preventDefault();
    focusRestAfterLevel.current?.focus({ preventScroll: true });
  };
  const { levelSelect, levelCompletedAt } = props;
  const p = props.data;
  const pos = { left: p.left, top: p.top };
  const complete = p.levelStatus === LevelStatus.complete;
  const inProgress = p.levelStatus === LevelStatus.active;
  let chosenStyles = {};
  if (levelCompletedAt) {
    chosenStyles = completeContainerStyles;
  } else if (inProgress) {
    chosenStyles = inProgressContainerStyles;
  }
  const numberColor = complete ? colors.color.completeNumber : colors.color.incompleteNumber;

  // The name is measured once laid out (and again when web fonts arrive or the
  // window changes size) and then drawn to fit: see fitLevelName.
  const { nameWrapWidth, pathAreaTop, pathAreaBottom } = props;
  const labelRef = useRef<HTMLDivElement | null>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);
  const nameSizesRef = useRef<HTMLDivElement | null>(null);
  const [nameFit, setNameFit] = useState<{ fit: LevelNameFit; baseFontSize: number } | null>(null);
  // The name's own size, once read: only the sizes it can be drawn at get a hidden line.
  const [nameBaseFontSize, setNameBaseFontSize] = useState<number | null>(null);
  const nameSizeSteps = useMemo(
    () => (nameBaseFontSize === null ? [0] : usableNameSizeSteps(nameBaseFontSize)),
    [nameBaseFontSize],
  );
  // Split where a browser may break the line; a no-break space stays inside its word.
  const nameWords = useMemo(() => p.levelName.split(/[ \t\n\r\f]+/).filter(Boolean), [p.levelName]);

  useLayoutEffect(() => {
    const measureName = () => {
      const label = labelRef.current;
      const spacer = spacerRef.current;
      const nameSizes = nameSizesRef.current;
      if (!label || !spacer || !nameSizes) return;
      const lineHeight = spacer.getBoundingClientRect().height;
      if (!(lineHeight > 0)) return;
      const baseFontSize = Number.parseFloat(window.getComputedStyle(label).fontSize) || 14;
      // Until the hidden lines match the sizes this name can be drawn at, they are drawn
      // again (before the page is painted) and measured then.
      setNameBaseFontSize(baseFontSize);
      const sizeRows = Array.from(nameSizes.querySelectorAll<HTMLElement>('[data-name-size]'));
      if (sizeRows.length !== usableNameSizeSteps(baseFontSize).length) return;
      const nodeTop = Number.parseFloat(p.top);
      const room =
        p.labelSide == 'top'
          ? nodeTop + labelPadding + lineHeight - pathAreaTop
          : pathAreaBottom - (nodeTop + circleSize + labelPadding);
      const [atBase, ...smaller] = sizeRows.map((row) => {
        const { width, height } = row.getBoundingClientRect();
        return {
          fontSize: baseFontSize - Number(row.dataset.nameSize),
          oneLineWidth: width,
          wordWidths: Array.from(
            row.querySelectorAll('[data-name-word]'),
            (word) => word.getBoundingClientRect().width,
          ),
          lineHeight: height,
        };
      });
      if (!atBase) return;
      const fit = fitLevelName({ sizes: [atBase, ...smaller], maxWidth: nameWrapWidth, room });
      setNameFit((previous) =>
        previous &&
        previous.baseFontSize === baseFontSize &&
        previous.fit.fontSize === fit.fontSize &&
        previous.fit.wraps === fit.wraps &&
        previous.fit.pullTowardNode === fit.pullTowardNode
          ? previous
          : { fit, baseFontSize },
      );
    };
    measureName();
    let mounted = true;
    void document.fonts?.ready.then(() => mounted && measureName());
    window.addEventListener('resize', measureName);
    return () => {
      mounted = false;
      window.removeEventListener('resize', measureName);
    };
  }, [p.levelName, p.top, p.labelSide, nameWrapWidth, pathAreaTop, pathAreaBottom, nameSizeSteps]);

  // A hidden one-line spacer keeps the label exactly one line tall, so the
  // circle stays on the curve whatever the name does. The name's edge next to
  // the circle stays put; a second line grows away from the circle.
  const renderLabel = () => {
    const fit = nameFit?.fit;
    const wraps = fit ? fit.wraps : nameWrapWidth !== null;
    const nameOffset = `${labelPadding - (fit?.pullTowardNode ?? 0)}px`;
    const nameEdge = p.labelSide == 'top' ? { bottom: nameOffset } : { top: nameOffset };
    // Relative to the label's own size, so it follows the label if that changes.
    const shrunkFontSize =
      fit && nameFit && fit.fontSize < nameFit.baseFontSize
        ? { fontSize: `${fit.fontSize / nameFit.baseFontSize}em` }
        : {};
    return (
      <Box
        ref={labelRef}
        className="blob-text-styles"
        sx={{ color: colors.color.incomplete, position: 'relative' }}
      >
        <Box
          ref={spacerRef}
          data-name-spacer
          aria-hidden="true"
          sx={{ visibility: 'hidden', whiteSpace: 'nowrap' }}
        >
          {'\u00a0'}
        </Box>
        {/* Zero height, so the rows add nothing to the path scroller's scroll range. */}
        <Box
          ref={nameSizesRef}
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: 0,
            overflow: 'hidden',
            visibility: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {nameSizeSteps.map((step) => (
            <Box
              key={step}
              data-name-size={step}
              sx={{ width: 'max-content', fontSize: step ? `calc(1em - ${step}px)` : undefined }}
            >
              {nameWords.map((word, index) => (
                <Fragment key={index}>
                  {index > 0 && ' '}
                  <span data-name-word>{word}</span>
                </Fragment>
              ))}
            </Box>
          ))}
        </Box>
        <Box
          data-level-name
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'max-content',
            maxWidth: wraps && nameWrapWidth !== null ? `${nameWrapWidth}px` : 'none',
            whiteSpace: wraps ? 'normal' : 'nowrap',
            textAlign: 'center',
            ...shrunkFontSize,
            ...nameEdge,
          }}
        >
          {p.levelName}
        </Box>
      </Box>
    );
  };

  const handleSingleClick = (levelIndex: number) => {
    dispatch(appActions.setLevelSelect({ levelSelect: levelIndex }));
    props.onSingleSelect(levelIndex);
  };

  const handleDoubleClick = (levelIndex: number, levelNumber: number, tierComplete: number) => {
    dispatch(appActions.setLevelSelect({ levelSelect: levelIndex }));
    dispatch(appActions.fetchTutorialData());
    navigate(resolvePlayRoute(levelNumber, tierComplete, completedChapters));
  };

  const handleLevelSelect = (levelIndex: number, levelNumber: number, tierComplete: number) => {
    // On small phones and native touch, single tap should always select/open preview.
    if (props.forceSingleTap || isCapacitorNative()) {
      handleSingleClick(levelIndex);
      return;
    }

    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      handleDoubleClick(levelIndex, levelNumber, tierComplete);
    } else {
      const newTimeout = setTimeout(() => {
        handleSingleClick(levelIndex);
        setClickTimeout(null);
      }, 200); // double click delay
      setClickTimeout(newTimeout);
    }
  };

  return (
    <>
      <TouchBox
        data-roadmap-level-button={p.levelNumber}
        data-level-index={p.levelIndex}
        aria-label={`Select level ${p.levelNumber}: ${p.levelName}`}
        sx={{ ...nameContainerStyles, ...pos }}
        onClick={() => handleLevelSelect(p.levelIndex, p.levelNumber, p.highestTierComplete)}
        onPointerDown={notePointerType}
        onMouseDown={keepMousePressFromFocusing}
      >
        {p.labelSide == 'top' && renderLabel()}
        <Box
          className="no-select"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box
            className="level-blob-circle"
            sx={{ ...circleContainerStyles, ...chosenStyles, position: 'relative' }}
          >
            <Box sx={{ ...numberStyles, ...numberColor }}>{p.levelNumber}</Box>
            {p.levelIndex == levelSelect && <Box sx={selectedStyles} />}
          </Box>
        </Box>
        {p.labelSide == 'bottom' && renderLabel()}
      </TouchBox>
      <span ref={focusRestAfterLevel} tabIndex={-1} data-level-focus-rest style={focusRestStyle} />
    </>
  );
};

const focusRestStyle = {
  position: 'absolute',
  width: 0,
  height: 0,
  overflow: 'hidden',
  outline: 'none',
} as const;

interface PathProps {
  lastLevelComplete: number;
  userLevelStatuses: Record<string, string>;
  scrollPos: number;
  setScrollPos: React.Dispatch<React.SetStateAction<number>>;
  setScrollAreaSize: React.Dispatch<React.SetStateAction<number>>;
  levelSelect: number;
  unitSelect: number;
  levelData: LevelData[];
  userLevelTiersComplete: Record<number, number>;
  areaToPreventScroll: string;
  setAreaToPreventScroll: React.Dispatch<React.SetStateAction<string>>;
  pathScrollPos: number;
  userLevelData: UserLevelData[];
  latestLevelRef: React.RefObject<HTMLDivElement | null>;
  isSmallMobile: boolean;
  tierCountForLevel?: number;
}

export const Path = (props: PathProps) => {
  const {
    levelSelect,
    levelData,
    unitSelect,
    lastLevelComplete,
    userLevelStatuses,
    userLevelTiersComplete,
    areaToPreventScroll,
    setScrollPos,
    setScrollAreaSize,
    setAreaToPreventScroll,
    pathScrollPos,
    userLevelData,
    latestLevelRef,
    isSmallMobile,
    tierCountForLevel = 1,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get the last complete level:
  const latestLevelRefContainer = useRef<HTMLDivElement | null>(null);

  const [sheetState, setSheetState] = useState<LevelPreviewSheetState>('closed');
  const closeSheet = () => setSheetState('closed');
  // Android's Back closes the level sheet as a tap on the dimmed map does.
  // The sheet is drawn at z-index 1500 over a 1400 backdrop.
  useHardwareBackClosesLayer(isSmallMobile && sheetState !== 'closed', closeSheet, 1500);
  const [mapTranslateY, setMapTranslateY] = useState(0);
  const mapTranslateYRef = useRef(0);

  const [screenSizeScale, setScreenSizeScale] = useState<RoadmapPathScale>(
    roadmapPathScale(window.innerHeight),
  );

  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0,
  );
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [safeInsets, setSafeInsets] = useState({
    top: readSafeInset('--safe-area-top'),
    bottom: readSafeInset('--safe-area-bottom'),
  });

  useEffect(() => {
    mapTranslateYRef.current = mapTranslateY;
  }, [mapTranslateY]);

  useEffect(() => {
    const handleResize = () => {
      setScreenSizeScale(roadmapPathScale(window.innerHeight));
      setViewportHeight(window.innerHeight);
      setWindowWidth(window.innerWidth);
      setSafeInsets({
        top: readSafeInset('--safe-area-top'),
        bottom: readSafeInset('--safe-area-bottom'),
      });
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isSmallMobile) {
      setSheetState('closed');
      setMapTranslateY(0);
      mapTranslateYRef.current = 0;
    }
  }, [isSmallMobile]);

  const [blobPositions, _pathPositions, basePxArray, xBlobPosVals, _pathCompleteVals] =
    constGeneratePositions(screenSizeScale);

  // for where the roadmap path stops (visual display of completed path)
  // Handle -1 (no levels complete) case - show no completed path
  const curLevelPosition =
    lastLevelComplete >= 0
      ? (xBlobPosVals[xBlobPosVals.length - 1] + 60) *
          Math.floor(lastLevelComplete / xBlobPosVals.length) +
        xBlobPosVals[lastLevelComplete % xBlobPosVals.length] +
        60
      : 0;

  // Scroll position should center on the last selected level (shown by the ring), not highest completed
  const selectedLevelPosition =
    levelSelect >= 0
      ? (xBlobPosVals[xBlobPosVals.length - 1] + 60) *
          Math.floor(levelSelect / xBlobPosVals.length) +
        xBlobPosVals[levelSelect % xBlobPosVals.length] +
        60
      : 0;

  // pathScrollPos is how far along the map the minimap box is (0 = start,
  // 1 = end), so the box reaches the end of the minimap exactly when the path
  // reaches its end, whatever the window size.
  useEffect(() => {
    const scrollContainer = latestLevelRef.current;
    if (scrollContainer && areaToPreventScroll === 'path') {
      const maxScrollLeft = Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth);
      scrollContainer.scrollLeft = clamp(pathScrollPos, 0, 1) * maxScrollLeft;
    }
  }, [pathScrollPos, areaToPreventScroll, latestLevelRef]);

  useEffect(() => {
    const el = latestLevelRef.current;
    if (!el) return;

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const denom = Math.max(1, target.scrollWidth - target.clientWidth);

      // Only skip when the minimap is the driver (it sets areaToPreventScroll = 'path')
      if (areaToPreventScroll !== 'path') {
        setScrollPos(target.scrollLeft / denom);
      }

      setScrollAreaSize(target.clientWidth / Math.max(1, target.scrollWidth));
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [areaToPreventScroll, latestLevelRef, setScrollAreaSize, setScrollPos]);

  useEffect(() => {
    const el = latestLevelRef.current;
    if (!el) return;

    const timeoutId = window.setTimeout(() => {
      if (selectedLevelPosition) {
        const containerWidth = el.clientWidth;
        el.scrollTo({
          left: selectedLevelPosition - containerWidth / 2 + 35,
          behavior: 'smooth',
        });
      }
    }, 250);

    setScrollPos(el.scrollLeft / Math.max(1, el.scrollWidth - el.clientWidth));
    setScrollAreaSize(el.clientWidth / Math.max(1, el.scrollWidth));

    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const el = latestLevelRef.current;
    if (!el) return;
    const handleDragStart = (e: DragEvent) => e.preventDefault();
    el.addEventListener('dragstart', handleDragStart);
    return () => el.removeEventListener('dragstart', handleDragStart);
  }, [latestLevelRef]);

  useEffect(() => {
    const el = latestLevelRef.current;
    if (!el) return;
    const preventNavigation = (e: WheelEvent) => {
      if (e.deltaX < 0 && (e.target as HTMLDivElement).scrollLeft <= 0 && el.scrollLeft <= 0) {
        e.preventDefault();
      }
    };
    el.addEventListener('wheel', preventNavigation, { passive: false });
    return () => el.removeEventListener('wheel', preventNavigation);
  }, [latestLevelRef]);

  //let lastLevel = parseInt(Object.keys(Levels)[Object.keys(Levels).length -1])
  const lastLevel = levelData.length;

  // The left and right arrows move to the previous or next level: one listener for the
  // whole path, so a key press moves the selection once. When a level has focus, focus
  // moves with the selection, so Space or Enter then acts on the level now selected.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // A focused field or control that uses the arrows keeps them.
      if (!isArrowForThePage(event)) return;
      const step = event.key === 'ArrowRight' ? 1 : -1;
      const nextLevelIndex = levelSelect + step;
      if (nextLevelIndex < 0 || nextLevelIndex > lastLevel - 1) return;
      dispatch(appActions.setLevelSelect({ levelSelect: nextLevelIndex }));
      const levelHadFocus =
        event.target instanceof Element && event.target.closest('[data-level-index]') !== null;
      if (levelHadFocus) {
        latestLevelRef.current
          ?.querySelector<HTMLElement>(`[data-level-index="${nextLevelIndex}"]`)
          ?.focus({ preventScroll: true });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [levelSelect, dispatch, lastLevel, latestLevelRef]);
  const showsEndOfUnit = unitSelect == 1;
  // 12 levels can fit on one road map image, but need to load next image piece if on level 12.
  // One more piece when the path runs on past the last level.
  const numImagesShow = [...Array(Math.floor(lastLevel / 12) + (showsEndOfUnit ? 2 : 1))];
  // ^^ Need to convert that math value into an array range!
  // Determin where to cut off the combined roadmap images based on where the last level lies!
  let blobLevelCutoff;
  const numPaths = Math.floor(lastLevel / xBlobPosVals.length);
  let builtBlobPositions: Array<{ left: string; top: string; labelSide: string }>;

  const endOffset = endOffsetValues[screenSizeScale as keyof typeof endOffsetValues];
  if (numPaths == 0) {
    blobLevelCutoff = `${xBlobPosVals[xBlobPosVals.length - 1] + 50}px`;
    builtBlobPositions = blobPositions;
  } else {
    blobLevelCutoff = `${
      xBlobPosVals[xBlobPosVals.length - 1] * numPaths +
      xBlobPosVals[lastLevel % xBlobPosVals.length] +
      numPaths * 50 -
      endOffset
    }px`;
    builtBlobPositions = blobPositions;
    void [...Array(numPaths)].map((_k, v) => {
      let currentBasePxArray = basePxArray;
      if (v == numPaths - 1) {
        currentBasePxArray = {
          x: basePxArray.x.slice(0, lastLevel % xBlobPosVals.length),
          y: basePxArray.y.slice(0, lastLevel % xBlobPosVals.length),
        };
      }
      const newBlobPositions = convertToPos(
        currentBasePxArray,
        (v + 1) * xBlobPosVals[xBlobPosVals.length - 1] + (v + 1) * 60,
      );
      builtBlobPositions = builtBlobPositions.concat(newBlobPositions);
    });
  }

  // Names sit on alternate sides of the path, so a name can only run into the
  // names two levels before and after it. At the small scale a name may be as
  // wide as the gap to the nearer of those, less a gutter, before it wraps.
  const nameWrapWidths: Array<number | null> = builtBlobPositions.map((position, i) => {
    if (screenSizeScale !== 'small') return null;
    const gapsToSameSideNames = [i - 2, i + 2]
      .filter((j) => j >= 0 && j < builtBlobPositions.length)
      .map((j) =>
        Math.abs(Number.parseFloat(builtBlobPositions[j].left) - Number.parseFloat(position.left)),
      );
    if (!gapsToSameSideNames.length) return null;
    return Math.max(nameLabelWidth, Math.min(...gapsToSameSideNames) - nameGutter);
  });

  const selectedLevel = levelData[levelSelect] || levelData[0];
  const selectedLevelNumber = selectedLevel?.level_number ?? 0;
  const highestTierCompleteForSelected = userLevelTiersComplete[selectedLevelNumber] || 0;
  // Completed tutorial chapters for the selected level, read the same way the
  // Play bar reads them - see resolvePlayRoute / MUS-393.
  const completedChaptersForSelected = useSelector(
    (state: RootState) =>
      Immutable.get(state.tutorialReducer?.completed, selectedLevelNumber) as CompletedChapters,
  );

  // The sheet sizes itself to its content (title, tier segments, preview, Play)
  // up to this height, so it always comes up far enough to show all of it.
  const sheetMaxHeight = useMemo(
    () => Math.max(sheetPeekHeight + 36, viewportHeight - safeInsets.top - 16),
    [viewportHeight, safeInsets.top],
  );
  // In a portrait window (a phone held upright, on the web) the sheet at peek covers
  // no more than about half the window, so the selected level stays in view above it.
  const sheetPeekMaxHeight =
    viewportHeight > windowWidth
      ? Math.min(sheetMaxHeight, Math.round(viewportHeight * sheetPeekPortraitShare))
      : sheetMaxHeight;
  // Last measured height of the open sheet, for keeping the selected node above it.
  const [openSheetHeight, setOpenSheetHeight] = useState(sheetPeekHeight + safeInsets.bottom);

  const currentSheetHeight = sheetState === 'closed' ? 0 : openSheetHeight;

  const autoPanToLevel = useCallback(
    (selectedLevelIndex: number, sheetHeightOverride?: number) => {
      if (!isSmallMobile) return;

      const scrollContainer = latestLevelRef.current;
      if (!scrollContainer) return;

      const selectedDot = scrollContainer.querySelector(
        `[data-level-index="${selectedLevelIndex}"] .level-blob-circle`,
      ) as HTMLElement | null;
      if (!selectedDot) return;

      const dotRect = selectedDot.getBoundingClientRect();
      const screenX = dotRect.left + dotRect.width / 2;
      const screenY = dotRect.top + dotRect.height / 2;

      const viewportWidth = window.innerWidth;
      const viewportHeightNow = window.innerHeight;
      const safeTop = readSafeInset('--safe-area-top');

      const paddingSide = 16;
      const targetY = 0.38 * viewportHeightNow;

      // The part of the path area the sheet leaves uncovered: below the area's top edge
      // (and the safe area) and above the open sheet.
      const sheetHeight = sheetHeightOverride ?? currentSheetHeight;
      const pathArea = scrollContainer.getBoundingClientRect();
      const visibleBand = {
        top: Math.max(pathArea.top, safeTop),
        bottom: Math.min(pathArea.bottom, viewportHeightNow - sheetHeight),
      };
      const highestVisibleY = visibleBand.top + mapEdgePadding;
      const lowestVisibleY = visibleBand.bottom - mapEdgePadding;

      if (screenY > lowestVisibleY || screenY < highestVisibleY) {
        // Measured where the map is drawn now, which may be part way through a move.
        const translateYNow = drawnTranslateY(
          scrollContainer.firstElementChild,
          mapTranslateYRef.current,
        );
        const nodeCentres = Array.from(
          scrollContainer.querySelectorAll('.level-blob-circle'),
          (circle) => {
            const rect = circle.getBoundingClientRect();
            return rect.top + rect.height / 2;
          },
        );
        const desiredY = clamp(targetY, highestVisibleY, lowestVisibleY);
        const nextTranslateY = clampMapTranslateY(translateYNow + desiredY - screenY, {
          translateYNow,
          topNodeCentre: Math.min(...nodeCentres),
          bottomNodeCentre: Math.max(...nodeCentres),
          visibleBand,
          edgePadding: mapEdgePadding,
        });
        if (Math.abs(nextTranslateY - mapTranslateYRef.current) > 0.5) {
          mapTranslateYRef.current = nextTranslateY;
          setMapTranslateY(nextTranslateY);
        }
      }

      const desiredX = clamp(screenX, paddingSide, viewportWidth - paddingSide);
      if (Math.abs(desiredX - screenX) > 0.5) {
        const deltaScroll = screenX - desiredX;
        const maxScrollLeft = Math.max(
          0,
          scrollContainer.scrollWidth - scrollContainer.clientWidth,
        );
        const nextScrollLeft = clamp(scrollContainer.scrollLeft + deltaScroll, 0, maxScrollLeft);
        scrollContainer.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
      }
    },
    [currentSheetHeight, isSmallMobile, latestLevelRef],
  );

  useEffect(() => {
    if (!isSmallMobile || sheetState === 'closed') return;

    const timeoutId = window.setTimeout(() => {
      autoPanToLevel(levelSelect);
    }, 40);

    return () => window.clearTimeout(timeoutId);
  }, [autoPanToLevel, levelSelect, isSmallMobile, sheetState, currentSheetHeight]);

  // Functions that allow clicking and dragging the mouse
  // Dragging the map with the mouse, from anywhere on it (a level included): the map
  // follows the pointer from where the press began. A press that moves no more than a
  // few pixels is a click; once it moves further, the click that ends it is not a click
  // on the level under the pointer.
  const mapDrag = useRef<{ originX: number; originScrollLeft: number } | null>(null);
  const draggedTheMap = useRef(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    mapDrag.current = {
      originX: e.clientX,
      originScrollLeft: latestLevelRef.current?.scrollLeft ?? 0,
    };
    draggedTheMap.current = false;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (areaToPreventScroll != 'minimap') {
      setAreaToPreventScroll('minimap');
    }
    const drag = mapDrag.current;
    const scrollContainer = latestLevelRef.current;
    if (!drag || !scrollContainer) return;
    // The button was let go outside the map.
    if ((e.buttons & 1) === 0) {
      mapDrag.current = null;
      return;
    }
    const travel = e.clientX - drag.originX;
    if (!draggedTheMap.current && Math.abs(travel) <= mapDragClickSlop) return;
    draggedTheMap.current = true;
    scrollContainer.scrollLeft = drag.originScrollLeft - travel;
  };

  // Only the click that ends this press is a drag's: one that comes later without a
  // press (from a script or assistive technology) is a click again.
  const forgetTheDragSoon = () => {
    window.setTimeout(() => {
      draggedTheMap.current = false;
    }, 0);
  };

  const handleMouseUp = () => {
    mapDrag.current = null;
    forgetTheDragSoon();
  };

  // A drag that leaves the map ends there, but its button is still down: the press is
  // over (and its click, if any, stopped) only when the button comes up, over the map
  // or anywhere else.
  const releaseAfterLeaving = useRef<(() => void) | null>(null);
  const handleMouseLeave = () => {
    mapDrag.current = null;
    if (!draggedTheMap.current || releaseAfterLeaving.current) return;
    const onRelease = () => {
      releaseAfterLeaving.current = null;
      forgetTheDragSoon();
    };
    releaseAfterLeaving.current = onRelease;
    window.addEventListener('mouseup', onRelease, { once: true });
  };
  useEffect(
    () => () => {
      if (releaseAfterLeaving.current) {
        window.removeEventListener('mouseup', releaseAfterLeaving.current);
      }
    },
    [],
  );

  const keepDragFromClickingALevel = (e: React.MouseEvent) => {
    if (!draggedTheMap.current) return;
    draggedTheMap.current = false;
    e.stopPropagation();
  };

  const onPlaySelectedLevel = useCallback(() => {
    dispatch(appActions.fetchTutorialData());
    navigate(
      resolvePlayRoute(
        selectedLevelNumber,
        highestTierCompleteForSelected,
        completedChaptersForSelected,
      ),
    );
  }, [
    completedChaptersForSelected,
    dispatch,
    highestTierCompleteForSelected,
    navigate,
    selectedLevelNumber,
  ]);

  const onLevelSingleSelect = useCallback(
    (selectedLevelIndex: number) => {
      if (!isSmallMobile) return;

      const nextSheetState = sheetState === 'closed' ? 'peek' : sheetState;
      if (nextSheetState !== sheetState) {
        setSheetState(nextSheetState);
      }

      window.setTimeout(() => {
        autoPanToLevel(selectedLevelIndex, openSheetHeight);
      }, 40);
    },
    [autoPanToLevel, isSmallMobile, openSheetHeight, sheetState],
  );

  const pathImageHeight = roadmapPathImageHeight(screenSizeScale);
  const pathDrawnWidth = showsEndOfUnit
    ? `calc(${blobLevelCutoff} + ${endOfUnitFadeWidth}px)`
    : blobLevelCutoff;
  const pathRunOutFade = showsEndOfUnit
    ? {
        maskImage: `linear-gradient(to right, #000 calc(100% - ${endOfUnitFadeWidth}px), transparent)`,
        WebkitMaskImage: `linear-gradient(to right, #000 calc(100% - ${endOfUnitFadeWidth}px), transparent)`,
      }
    : {};

  const pathRootRef = useRef<HTMLDivElement | null>(null);
  const mapLayerRef = useRef<HTMLDivElement | null>(null);
  const endOfUnitAnchorRef = useRef<HTMLDivElement | null>(null);
  const endOfUnitLayerRef = useRef<HTMLDivElement | null>(null);
  const comingSoonRef = useRef<HTMLDivElement | null>(null);

  // The dark layer is fixed to the window (so it can run from the top of the
  // window to the bottom) and follows the end of the path as the map scrolls.
  // It never reaches left of the roadmap's own area, wherever the sidebar is.
  const placeEndOfUnitLayer = useCallback(() => {
    const anchor = endOfUnitAnchorRef.current;
    const layer = endOfUnitLayerRef.current;
    const comingSoon = comingSoonRef.current;
    const scrollContainer = latestLevelRef.current;
    if (!anchor || !layer || !comingSoon || !scrollContainer) return;

    const anchorRect = anchor.getBoundingClientRect();
    const roadmapAreaLeft = Math.max(
      scrollContainer.getBoundingClientRect().left,
      pathRootRef.current?.parentElement?.getBoundingClientRect().left ?? -Infinity,
    );
    const layerLeft = clamp(anchorRect.left, roadmapAreaLeft, window.innerWidth);
    const fadeStart = anchorRect.left - layerLeft;

    layer.style.left = `${layerLeft}px`;
    layer.style.backgroundImage = endOfUnitShade(fadeStart);
    comingSoon.style.left = `${fadeStart + endOfUnitFadeWidth / 2}px`;
    comingSoon.style.top = `${anchorRect.top + anchorRect.height / 2}px`;
  }, [latestLevelRef]);

  useEffect(() => {
    if (!showsEndOfUnit) return;
    const scrollContainer = latestLevelRef.current;
    const mapLayer = mapLayerRef.current;
    const place = () => placeEndOfUnitLayer();
    place();

    scrollContainer?.addEventListener('scroll', place, { passive: true });
    mapLayer?.addEventListener('transitionend', place);
    window.addEventListener('resize', place);
    window.addEventListener('orientationchange', place);
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    if (scrollContainer) resizeObserver?.observe(scrollContainer);

    return () => {
      scrollContainer?.removeEventListener('scroll', place);
      mapLayer?.removeEventListener('transitionend', place);
      window.removeEventListener('resize', place);
      window.removeEventListener('orientationchange', place);
      resizeObserver?.disconnect();
    };
  }, [
    showsEndOfUnit,
    placeEndOfUnitLayer,
    latestLevelRef,
    blobLevelCutoff,
    pathImageHeight,
    mapTranslateY,
  ]);

  return (
    <Box className="path-container-styles" ref={pathRootRef}>
      <Box ref={latestLevelRefContainer}>
        <Box
          className="chatbox-scroll-container"
          style={{
            width: '100%',
            position: 'relative',
            paddingBottom: `${pathScrollerPadding}px`,
            paddingTop: `${pathScrollerPadding}px`,
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
          ref={latestLevelRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onClickCapture={keepDragFromClickingALevel}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <Box
            ref={mapLayerRef}
            sx={{
              transform: `translateY(${mapTranslateY}px)`,
              transition: 'transform 200ms ease-out',
              willChange: 'transform',
            }}
          >
            <Box sx={{ position: 'absolute' }}>
              {builtBlobPositions.map(
                (e: { left: string; top: string; labelSide: string }, i: number) => {
                  const levelNum: number =
                    unitSelect === 2 && levelData.length >= i + 26 ? i + 26 : i;
                  const levelStatus =
                    find(userLevelStatuses, (_, key) => key == `${i}`) || 'inactive';
                  const highestTierComplete = userLevelTiersComplete[levelNum]
                    ? userLevelTiersComplete[levelNum]
                    : 0;
                  let isAccessible = false;
                  const levelName = find(
                    levelData,
                    (datum: LevelData) => datum.level_number === levelNum,
                  )?.name;
                  const levelCompletedAt = find(
                    userLevelData,
                    (datum: UserLevelData) => datum?.level?.level_number === levelNum,
                  )?.level_completed_at;
                  if (find(levelData, (datum: LevelData) => datum?.level_number === levelNum)) {
                    isAccessible = true;
                  } else {
                    return <Box key={`blob-${i}`}></Box>;
                  }
                  return (
                    <Box key={`blob-${i}`}>
                      <LevelBlob
                        levelCompletedAt={levelCompletedAt}
                        levelSelect={levelSelect}
                        onSingleSelect={onLevelSingleSelect}
                        forceSingleTap={isSmallMobile}
                        nameWrapWidth={nameWrapWidths[i] ?? null}
                        pathAreaTop={-pathScrollerPadding}
                        pathAreaBottom={pathImageHeight + pathScrollerPadding}
                        data={{
                          ...e,
                          levelIndex: i,
                          levelNumber: levelNum,
                          levelName: levelName || '',
                          levelStatus: levelStatus,
                          highestTierComplete: highestTierComplete,
                          isAccessible: isAccessible,
                        }}
                      />
                    </Box>
                  );
                },
              )}
            </Box>

            <Box
              sx={{
                width: blobLevelCutoff,
                position: 'relative',
                display: 'flex',
                flexDirection: 'row',
                marginRight: '100px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: pathDrawnWidth,
                  flexShrink: 0,
                  overflow: 'hidden',
                  position: 'relative',
                  ...pathRunOutFade,
                }}
              >
                {numImagesShow.map((_k, v) => {
                  return (
                    <Box key={`img-${v}`}>
                      <img
                        className="roadmap-img-styles"
                        src={RoadmapPath}
                        style={{
                          height: pathImageHeight,
                        }}
                      />
                    </Box>
                  );
                })}

                <Box
                  sx={{
                    position: 'absolute',
                    width: `${curLevelPosition}px`,
                    overflow: 'hidden',
                    display: 'flex',
                  }}
                >
                  {numImagesShow.map((_k, v) => {
                    return (
                      <Box key={`img-complete-${v}`}>
                        <img
                          className="roadmap-img-styles"
                          src={RoadmapPathComplete}
                          style={{
                            height: pathImageHeight,
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {showsEndOfUnit && (
                <Box
                  ref={endOfUnitAnchorRef}
                  data-end-of-unit-anchor
                  sx={{
                    position: 'absolute',
                    left: '100%',
                    top: 0,
                    bottom: 0,
                    width: `${endOfUnitFadeWidth}px`,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {showsEndOfUnit && (
        <Box
          ref={endOfUnitLayerRef}
          data-testid="end-of-unit-layer"
          sx={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: '100%',
            right: 0,
            zIndex: endOfUnitLayerZIndex,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <Box
            ref={comingSoonRef}
            sx={{
              position: 'absolute',
              transform: 'translate(-50%, -50%)',
              whiteSpace: 'nowrap',
              color: '#CED0D4',
              textAlign: 'center',
              fontFamily: 'Lato',
              fontSize: '24px',
              fontWeight: '400',
              lineHeight: 'normal',
              letterSpacing: '0.24px',
            }}
          >
            Unit 2 Coming Soon!
          </Box>
        </Box>
      )}

      {isSmallMobile && sheetState !== 'closed' && (
        <Box
          onClick={closeSheet}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            transition: 'opacity 200ms ease-out',
          }}
        />
      )}

      {isSmallMobile && (
        <LevelPreviewSheet
          state={sheetState}
          level={selectedLevel}
          maxHeight={sheetMaxHeight}
          peekMaxHeight={sheetPeekMaxHeight}
          viewportHeight={viewportHeight}
          safeBottom={safeInsets.bottom}
          highestTierComplete={highestTierCompleteForSelected}
          tierCountForLevel={tierCountForLevel}
          onStateChange={(nextState) => {
            setSheetState(nextState);
          }}
          onHeightChange={setOpenSheetHeight}
          onPlay={onPlaySelectedLevel}
        />
      )}
    </Box>
  );
};

// Things needed for the roadmap... make it continuous for all levels
// Roadmap is fixed png, so can generate the positions in a cyclical maner
// The path position should just be a function of that

const constGeneratePositions = (
  screenSizeScale: RoadmapPathScale,
): [
  Array<{ left: string; top: string; labelSide: string }>,
  Array<{ width: string }>,
  BasePxArray,
  number[],
  number[],
] => {
  // We'll call this the medium.
  let basePxArray = {
    x: [92, 265, 422, 576, 750, 886, 1022, 1173, 1320, 1460, 1620, 1766], // This will continue as fixed values until the loop pattern repeasts
    y: [305, 10, 212, -60, 305, 55, 368, -37, 280, 15, 310, 110], // Same as above
  };

  if (screenSizeScale == 'small') {
    const heightReductionFactor = 0.502;
    const horizontalOffset = 33;
    const verticalOffset = 19;
    basePxArray = {
      x: basePxArray.x.map((value) => value * heightReductionFactor - horizontalOffset),
      y: basePxArray.y.map((value) => value * heightReductionFactor - verticalOffset),
    };
  } else if (screenSizeScale == 'medium-small') {
    const heightReductionFactor = 0.805;
    const horizontalOffset = 20;
    const verticalOffset = 10;
    basePxArray = {
      x: basePxArray.x.map((value) => value * heightReductionFactor - horizontalOffset),
      y: basePxArray.y.map((value) => value * heightReductionFactor - verticalOffset),
    };
  } else if (screenSizeScale == 'large') {
    const heightReductionFactor = 1.205;
    const horizontalOffset = -3;
    const verticalOffset = -8;
    basePxArray = {
      x: basePxArray.x.map((value) => value * heightReductionFactor - horizontalOffset),
      y: basePxArray.y.map((value) => value * heightReductionFactor - verticalOffset),
    };
  }

  const pathOffsetVAlue = 35;
  const pathCompleteVals = basePxArray.x.map((v) => {
    return v + pathOffsetVAlue;
  });

  const blobPositions = convertToPos(basePxArray);
  const pathPositions = pathCompleteVals.map((v) => ({
    width: `${v}px`,
  }));
  const xBlobPosVals = basePxArray.x;
  return [blobPositions, pathPositions, basePxArray, xBlobPosVals, pathCompleteVals];
};

interface BasePxArray {
  x: number[];
  y: number[];
}

const convertToPos = (basePxArray: BasePxArray, offset = 0) => {
  const blobPositions = basePxArray.x.map((_e: number, i: number) => {
    const labelSide = i % 2 == 0 ? 'bottom' : 'top'; //label side will flip flop inperpetuity
    return {
      left: `${basePxArray.x[i] + offset}px`,
      top: `${basePxArray.y[i]}px`,
      labelSide: labelSide,
    };
  });
  return blobPositions;
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const colors = {
  background: {
    complete: { backgroundColor: '#28856F' },
    inProgress: { backgroundColor: '#414753' },
    incomplete: { backgroundColor: '#5B646E' },
  },
  color: {
    // The number on a completed (brand green) circle: white and bold, so it is large text, which
    // reads at 4.49:1 on the green (WCAG AA asks 3:1 of large text). The dark grey was 2.08:1.
    completeNumber: { color: '#FFFFFF', fontWeight: '700' },
    incomplete: { color: '#CED0D4' },
    // The number on a level circle not yet completed: 4.5:1 or more on its grey circle (WCAG AA).
    incompleteNumber: { color: '#E8E9EB' },
    selected: {
      background: '#CA7C62',
      cursor: 'pointer',
      transition: '0.3s',
      boxShadow: '0px 0px 5px 5px rgb(202,124,98,.8)',
    },
  },
};

const levelButtonFeedbackStyles = {
  cursor: 'pointer',
  touchAction: 'manipulation',
  WebkitTapHighlightColor: 'transparent',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transition: 'box-shadow 0.15s ease',
      boxShadow: '0px 0px 5px 5px rgb(70, 159, 106,.5)',
    },
  },
  '&:active': {
    boxShadow: '0px 0px 5px 5px rgb(70, 159, 106,.5)',
  },
};

const nameContainerStyles = {
  position: 'absolute',
  zIndex: 11,
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const circleContainerStyles = {
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: colors.background.incomplete,
  ...levelButtonFeedbackStyles,
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const inProgressContainerStyles = {
  backgroundColor: colors.background.inProgress,
  border: '5px solid #28856F',
  boxSizing: 'border-box',
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const completeContainerStyles = {
  backgroundColor: colors.background.complete,
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const selectedStyles = {
  width: '62px',
  height: '62px',
  borderRadius: '50%',
  backgroundColor: 'rgba(70, 159, 106, 0.3)',
  border: '1px solid rgba(70, 159, 106, .8)',
  position: 'absolute',
  zIndex: -1,
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const numberStyles = {
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '20px',
};
